import { DOMNode, Element, Text } from 'html-react-parser';

type DocumentationParserNode =
  | DOMNode
  | NonNullable<Element['parent']>;

export function getElementChildNodes(domElement: Element): DOMNode[] {
  return domElement.children as DOMNode[];
}

export function isHtmlElement(
  domNode: DocumentationParserNode,
): domNode is Element {
  return (
    domNode.type === 'tag' &&
    'name' in domNode &&
    typeof domNode.name === 'string'
  );
}

export function isElementName(
  domNode: DocumentationParserNode | null | undefined,
  elementName: string,
): boolean {
  return Boolean(
    domNode && isHtmlElement(domNode) && domNode.name === elementName,
  );
}

export function getDomNodeText(domNode: DOMNode): string {
  if (isTextNode(domNode)) {
    return domNode.data;
  }

  if (!isHtmlElement(domNode)) {
    return '';
  }

  return getElementChildNodes(domNode)
    .map(getDomNodeText)
    .join('');
}

export function findChildElementByClassName(
  domElement: Element,
  className: string,
): Element | undefined {
  for (const childNode of getElementChildNodes(domElement)) {
    if (
      isHtmlElement(childNode) &&
      (childNode.attribs?.class ?? '').includes(className)
    ) {
      return childNode;
    }
  }

  return undefined;
}

export function headingContainsAnchor(domElement: Element): boolean {
  return getElementChildNodes(domElement).some(containsAnchor);
}

export function isInsideFeatureHeading(domElement: Element): boolean {
  let ancestorNode = domElement.parent;

  while (ancestorNode) {
    if (
      isHtmlElement(ancestorNode) &&
      isFeatureHeadingName(ancestorNode.name)
    ) {
      return headingContainsAnchor(ancestorNode);
    }

    ancestorNode = ancestorNode.parent;
  }

  return false;
}

function containsAnchor(domNode: DOMNode): boolean {
  if (isHtmlElement(domNode) && domNode.name === 'a') {
    return true;
  }

  if (!isHtmlElement(domNode)) {
    return false;
  }

  return getElementChildNodes(domNode).some(containsAnchor);
}

function isFeatureHeadingName(elementName: string): boolean {
  return (
    elementName === 'h2' ||
    elementName === 'h3' ||
    elementName === 'h4' ||
    elementName === 'h5' ||
    elementName === 'h6'
  );
}

function isTextNode(domNode: DOMNode): domNode is Text {
  return domNode.type === 'text';
}
